import type { ResponseCookie } from 'next/dist/compiled/@edge-runtime/cookies'
import type { NextRequest, NextResponse } from 'next/server'

import { ONE_YEAR_IN_SECONDS } from '@src/utils/constants.util'

export function getBrowserCookie(name: string) {
  if (typeof document === 'undefined') return undefined
  const cookie = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`))
  if (!cookie) return undefined

  // A malformed value reads as absent; throwing from a mount effect would take down the page.
  try {
    return decodeURIComponent(cookie.split('=')[1])
  } catch {
    return undefined
  }
}

/** Encodes `value` so `getBrowserCookie` and Next.js `cookies()` decode it back verbatim. */
export function setBrowserCookie(name: string, value: string, options?: string) {
  if (typeof document === 'undefined') return

  const existingValue = getBrowserCookie(name)
  if (existingValue) {
    // Delete the existing cookie that does not set the domain
    removeBrowserCookie(name)
  }

  // Set the browser cookie with the domain
  const domain = getBrowserCookieDomain()
  const defaultOptions = `path=/; max-age=${ONE_YEAR_IN_SECONDS}`
  document.cookie = `${name}=${encodeURIComponent(value)}; domain=${domain}; ${options ?? defaultOptions};`
}

export function removeBrowserCookie(name: string) {
  if (typeof document === 'undefined') return
  const domain = getBrowserCookieDomain()
  document.cookie = `${name}=; path=/; max-age=0; domain=${domain};`
}

export function resetCookie(name: string, options?: string) {
  if (typeof document === 'undefined') return
  const decodedCookieValue = getBrowserCookie(name)
  if (!decodedCookieValue) return
  document.cookie = `${name}=${decodedCookieValue}; ${options ?? ''};`
}

function serializeCookieOptions(options: Partial<ResponseCookie>): string {
  return Object.entries(options ?? {})
    .map(([key, value]) => `${key}=${value}`)
    .join('; ')
}

/**
 * Isomorphic version of Next.js cookies API.
 * @see https://nextjs.org/docs/app/api-reference/functions/cookies
 */
export function isomorphicCookies(args?: { request?: NextRequest; response?: NextResponse }) {
  const isBrowser = typeof window !== 'undefined'
  const isEdgeRuntime = typeof EdgeRuntime !== 'undefined'
  return {
    get: async (name: string) => {
      if (isBrowser) {
        return getBrowserCookie(name)
      } else if (isEdgeRuntime) {
        if (!args?.request)
          throw new Error('[isomorphicCookies] request is required in Edge Runtime')
        return args?.request?.cookies.get(name)?.value
      } else {
        const { cookies } = await import('next/headers')
        const cookieStore = await cookies()
        return cookieStore.get(name)?.value
      }
    },
    set: async (name: string, value: string, options?: Partial<ResponseCookie>) => {
      if (isBrowser) {
        const defaultOptions: Partial<ResponseCookie> = { path: '/', maxAge: ONE_YEAR_IN_SECONDS }
        document.cookie = `${name}=${value}; ${serializeCookieOptions({ ...defaultOptions, ...options })};`
      } else if (isEdgeRuntime) {
        if (!args?.response)
          throw new Error('[isomorphicCookies] response is required in Edge Runtime')
        args?.response?.cookies.set(name, value, options)
      } else {
        const { cookies } = await import('next/headers')
        const cookieStore = await cookies()
        cookieStore.set(name, value, options)
      }
    },
    delete: async (name: string) => {
      if (isBrowser) {
        document.cookie = `${name}=; path=/; max-age=0;`
      } else if (isEdgeRuntime) {
        if (!args?.response)
          throw new Error('[isomorphicCookies] response is required in Edge Runtime')
        args?.response?.cookies.delete(name)
      } else {
        const { cookies } = await import('next/headers')
        const cookieStore = await cookies()
        cookieStore.delete(name)
      }
    },
    has: async (name: string) => {
      if (isBrowser) {
        const cookie = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`))
        return !!cookie
      } else if (isEdgeRuntime) {
        if (!args?.request)
          throw new Error('[isomorphicCookies] request is required in Edge Runtime')
        return args?.request?.cookies.has(name)
      } else {
        const { cookies } = await import('next/headers')
        const cookieStore = await cookies()
        return cookieStore.has(name)
      }
    },
  }
}
export function getBrowserCookieDomain() {
  if (typeof window === 'undefined') {
    return '.bodi.com'
  }
  // .bodi.com for prod, .{env}.bodi.com for non-prod
  const env = process.env.NEXT_PUBLIC_ENV ?? 'local'
  const sliceIndex = env === 'prod' ? -2 : -3
  const hostname = location.hostname.toLowerCase()
  if (hostname === 'bodi.com' || hostname.endsWith('.bodi.com')) {
    return hostname.split('.').slice(sliceIndex).join('.')
  } else {
    return hostname
  }
}

/**
 * Get environment-prefixed cookie name
 * @param cookieName - The base cookie name
 * @returns The prefixed cookie name for non-production environments, or the original name for production
 */
function getEnvPrefixedCookieName(cookieName: string = '') {
  const cookiePref = process.env.NEXT_PUBLIC_COOKIE_PREFIX ?? ''
  const env = process.env.NEXT_PUBLIC_ENV ?? 'local'
  const shouldOmitPrefix = cookiePref === 'null' || env === 'prod'

  if (shouldOmitPrefix) return cookieName

  const prefix = cookiePref || env
  const prefixedCookieName = `${prefix}-${cookieName}`

  return prefixedCookieName
}

/**
 * Helper function to create environment-prefixed cookies object
 * @param cookieNames - Object with cookie names as values
 * @returns Object with environment-prefixed cookie names
 */
export function createPrefixedCookies<T extends Record<string, string>>(cookieNames: T): T {
  const result = {} as Record<string, string>
  for (const [key, value] of Object.entries(cookieNames)) {
    result[key] = getEnvPrefixedCookieName(value)
  }
  return result as T
}
